import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import lobbyLogo from "../../assets/images/soundvista-wordmark.svg";
import signinBg from "../../assets/images/signin-bg.png";
import { IconGoogle, IconApple } from '../icons';
import { useAuth } from "../../context/AuthContext";
import "./signinscreen.scss";

const getAuthError = (err) => {
  const code = err?.code || '';
  const message = err?.message || '';
  const normalizedMessage = message.toLowerCase();

  if (code.includes('user-not-found') || code.includes('wrong-password') || code.includes('invalid-credential')) {
    return 'Invalid email or password.';
  }
  if (normalizedMessage.includes('invalid credentials')) return 'Invalid credentials.';
  if (normalizedMessage.includes('popup') && normalizedMessage.includes('blocked')) {
    return 'Popup was blocked. Allow popups and try again.';
  }
  if (code.includes('email-already-in-use')) return 'An account with this email already exists.';
  if (code.includes('weak-password')) return 'Password must be at least 6 characters.';
  if (code.includes('too-many-requests')) return 'Too many attempts. Please try again later.';
  if (code.includes('network-request-failed')) return 'Network error. Check your connection.';
  if (code.includes('popup-blocked')) return 'Popup was blocked. Allow popups and try again.';
  return 'Something went wrong. Please try again.';
};

export default function SignInScreen({ onSignInSuccess, initialMode = 'signin' }) {
  const navigate = useNavigate();
  const { signInWithGoogle, signInWithApple, signUpWithEmail, signInWithEmail } = useAuth();
  const [step, setStep] = useState(1);
  const isSignUp = initialMode === 'signup';
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setStep(1);
    setEmail('');
    setPassword('');
    setError('');
  }, [initialMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (step === 1) {
      if (!email.trim()) {
        setError("Please enter your email address.");
        return;
      }
      setStep(2);
      return;
    }

    // Step 2: sign up or sign in based on mode
    if (!password) {
      setError("Please enter your password.");
      return;
    }

    setLoading(true);
    try {
      const trimmedEmail = email.trim();
      const authFn = isSignUp ? signUpWithEmail : signInWithEmail;
      const result = await authFn(trimmedEmail, password);
      onSignInSuccess?.(result?.user);
    } catch (err) {
      setError(getAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setLoading(true);
    try {
      const result = await signInWithGoogle();
      onSignInSuccess?.(result?.user);
    } catch (err) {
      setError(getAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setPassword("");
    setError("");
  };

  return (
    <div className="signin-screen-wrapper">
      <img className="signin-bg" src={signinBg} alt="" />
      <div className="signin-bg-overlay" />

      <div className="signin-screen">
        <div className="signin-card">
          {/* Header */}
          <div className="signin-card__header">
            <img className="signin-card__logo" src={lobbyLogo} alt="Sound Vista" />
            <div className="signin-card__text">
              <h2>{isSignUp ? 'Create your account.' : 'Step into Sound Vista.'}</h2>
              <p>
                Discover immersive rooms, collect meaningful moments, and connect
                through music, media, and culture.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="signin-card__actions">
            <form className="signin-card__form" onSubmit={handleSubmit}>
              <div className="signin-card__input-group">
                <label className="signin-card__input-label">
                  {step === 1 ? "Email" : "Password"}
                </label>
                <div className="signin-card__input-field">
                  {step === 1 ? (
                    <input
                      type="email"
                      placeholder="Email Address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                    />
                  ) : (
                    <input
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete={isSignUp ? "new-password" : "current-password"}
                    />
                  )}
                </div>
                {step === 2 && (
                  <button type="button" className="signin-card__back-link" onClick={handleBack}>
                    Back
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="signin-card__continue-btn"
                disabled={loading}
              >
                {loading ? "Loading..." : "Continue"}
              </button>
            </form>

            <p className="signin-card__privacy">
              <span className="signin-card__privacy-line">
                By clicking Continue, you agree to Soundvista's privacy notice,
                T&Cs and to receive
              </span>
              <span className="signin-card__privacy-line">
                offers, news and updates.
              </span>
            </p>

            <div className="signin-card__divider">
              <span className="signin-card__divider-line left" />
              <span className="signin-card__divider-text">or</span>
              <span className="signin-card__divider-line right" />
            </div>

            <div className="signin-card__social">
              <button
                type="button"
                className="signin-card__social-btn"
                onClick={handleGoogleSignIn}
                disabled={loading}
              >
                <IconGoogle />
                Continue with Google
              </button>
              <button
                type="button"
                className="signin-card__social-btn"
                onClick={async () => {
                  setError("");
                  setLoading(true);
                  try {
                    const result = await signInWithApple();
                    onSignInSuccess?.(result?.user);
                  } catch (err) {
                    setError(getAuthError(err));
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
              >
                <IconApple />
                Continue with Apple
              </button>
            </div>

            {error && <p className="signin-card__error">{error}</p>}

            <div className="signin-card__footer">
              {isSignUp ? (
                <>
                  <span>Already have an account?</span>
                  <button type="button" className="signin-card__login-link" onClick={() => navigate('/signin')}>Log in</button>
                </>
              ) : (
                <>
                  <span>Don't have an account?</span>
                  <button type="button" className="signin-card__login-link" onClick={() => navigate('/signup')}>Sign up</button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
